import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useLocation } from "react-router-dom"
import { Container, Table, Alert, Button } from "reactstrap"
import { useMutation, useQuery } from "@tanstack/react-query"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"

import storage from "services/storage"
import { authUserKey } from "config/API"
import { getCustomers, deleteCustomer } from "services/customers"
import { Loading } from "components"
import { CreateModalForm, EditModalForm, ImportModalForm } from "components/customers"
import { useDownloadTemplate } from "hooks/use-download-template"
import ListHeader from "ui-componets/list-header"
import CustomersItemList from "components/customers/customers-item-list"
import Shortcuts from "components/Shortcuts"
import "assets/scss/custom/components/table-interactive.scss"

const PAGE_SIZE = 50

const CustomersList = () => {
  document.title = "Lista de clientes | Punto de venta"

  const Location = useLocation()

  const [searchTxt, setSearchTxt] = useState(new URLSearchParams(Location.search).get("search") ?? "")
  const [searchTerm, setSearchTerm] = useState(new URLSearchParams(Location.search).get("search") ?? "")
  const [currentPage, setCurrentPage] = useState(1)
  const [modalCreate, setmodalCreate] = useState(false)
  const [modalImport, setmodalImport] = useState(false)
  const [modalEdit, setmodalEdit] = useState(false)
  const [customerId, setCustomerId] = useState(null)
  const [showAlertSuccess, setShowAlertSuccess] = useState(false)
  const [msgSuccess, setMsgSuccess] = useState("")

  const ModalCustomer = withReactContent(Swal)

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer)
      toast.addEventListener("mouseleave", Swal.resumeTimer)
    },
  })

  // Espera 300ms para hacer la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1)
      setSearchTerm(searchTxt.trim())
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTxt])

  const queryFilters = useMemo(
    () => ({
      offset: (currentPage - 1) * PAGE_SIZE,
      per_page: PAGE_SIZE,
      ...(searchTerm ? { search: searchTerm } : {}),
      ordering: "name",
    }),
    [currentPage, searchTerm]
  )

  const customers = useQuery(
    ["customers", queryFilters],
    () => getCustomers(queryFilters),
    { keepPreviousData: true }
  )
  const customerDeleted = useMutation((id) => deleteCustomer(id))

  const permissions = storage.get(authUserKey).user_permissions || []
  const canAdd = permissions.some((e) => e.codename === "add_customer")
  const canEdit = permissions.some((e) => e.codename === "change_customer")
  const canDelete = permissions.some((e) => e.codename === "delete_customer")
  const styleCenterTable = { textAlign: "center", verticalAlign: "middle" }

  const toggleModalCreate = () => {
    setmodalCreate(!modalCreate)
  }

  const toggleModalImport = () => {
    setmodalImport(!modalImport)
  }

  const toggleModalEdit = () => {
    if (modalEdit) {
      setCustomerId("")
    }
    setmodalEdit(!modalEdit)
  }

  const openEditModal = (customerId) => {
    setCustomerId(customerId)
    toggleModalEdit()
  }

  const openDeleteModal = (row) => {
    Swal.fire({
      title: `¿Está seguro que desea eliminar al cliente: <b>${row.name}</b>?`,
      text: "Este cambio no se podrá revertir",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Aceptar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        customerDeleted.mutateAsync(row?.id).then(() => {
          customers.refetch()
          Toast.fire("!Eliminado!", `El cliente <b>${row.name}</b> ha sido eliminado correctamente`, "success")
        })
      }
    })
  }

  const handleChangePage = (nextPage) => {
    if (nextPage < 1) return
    if (totalPages > 0 && nextPage > totalPages) return
    setCurrentPage(nextPage)
  }

  const downloadTemplate = useCallback(() => {
    useDownloadTemplate("/customers/download_template")
  }, [])

  const handleChangeSearch = (event) => {
    setSearchTxt(event.target.value)
  }

  const handleSearch = () => {
    setCurrentPage(1)
    setSearchTerm(searchTxt.trim())
  }

  const isLoading = customers.isLoading || customerDeleted.isLoading
  const totalCount = customers.data?.count || 0
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 1
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount)

  useEffect(() => {
    if (showAlertSuccess) {
      ModalCustomer.fire({
        icon: "success",
        title: msgSuccess,
        didOpen: () => {
          customers.refetch()
          setShowAlertSuccess(false)
        },
        confirmButtonText: "Aceptar",
        confirmButtonColor: "green",
      })
    }
  }, [showAlertSuccess])

  return !permissions.some((e) => e.codename === "view_customer") ? (
    <div className="page-content">
      <div className="text-center">
        <h1>No autorizado para ver esta pagina</h1>
      </div>
    </div>
  ) : (
    <React.Fragment>
      <div className="page-content">
        {canAdd && (
          <CreateModalForm
            isOpen={modalCreate}
            toggle={toggleModalCreate}
            refetch={customers.refetch}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        {canAdd && (
          <ImportModalForm
            isOpen={modalImport}
            toggle={toggleModalImport}
            refetch={customers.refetch}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        {canEdit && customerId && (
          <EditModalForm
            isOpen={modalEdit}
            toggle={toggleModalEdit}
            customerId={customerId}
            refetch={customers.refetch}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        <Container fluid>
          <Alert color="danger" isOpen={customers.isError}>
            {customers?.error?.errors?.map((error) => error?.message)}
          </Alert>

          <h2>Lista de Clientes</h2>
          <div className="bg-white p-3">
            <ListHeader
              canAdd={canAdd}
              handleOnChangeSearch={handleChangeSearch}
              handleSearch={handleSearch}
              searchTxt={searchTxt}
              toggleModalCreate={toggleModalCreate}
              toggleModalImport={toggleModalImport}
            />
            {!customers.error && isLoading ? (
              <div className="my-5 text-center">
                <Loading isLoading={isLoading} size={2} />
              </div>
            ) : customers.error && !isLoading ? (
              <div className="my-5 text-center">
                <h1>Error intenta mas tarde</h1>
              </div>
            ) : (
              <div style={{ marginTop: "30px" }} className="table-responsive">
                <Table className="table-hover-effect table-interactive">
                  <thead>
                    <tr>
                      <th style={styleCenterTable} className="text-cell">Nombre</th>
                      <th style={styleCenterTable} className="text-cell">Email</th>
                      <th style={styleCenterTable} className="text-cell">Teléfono</th>
                      <th style={styleCenterTable}>Código Postal</th>
                      <th style={styleCenterTable} className="text-cell">RFC</th>
                      <th style={styleCenterTable} className="money-cell">Límite de crédito</th>
                      <th style={styleCenterTable} className="money-cell">Días de crédito</th>
                      <th style={styleCenterTable}>Tipo de precio</th>
                      <th style={styleCenterTable}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers?.data?.results.length > 0 ? (
                      customers?.data?.results?.map((item) => {
                        return (
                          <CustomersItemList
                            key={item.id}
                            item={item}
                            canDelete={canDelete}
                            canEdit={canEdit}
                            openDeleteModal={openDeleteModal}
                            openEditModal={openEditModal}
                          />
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center">
                          No hay datos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <div className="d-flex justify-content-between align-items-center mt-2">
                  <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                    Mostrando {startItem}-{endItem} de {totalCount} clientes
                  </div>
                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      disabled={currentPage <= 1 || isLoading || customers.isFetching}
                      onClick={() => handleChangePage(currentPage - 1)}
                    >
                      Anterior
                    </Button>
                    <span style={{ minWidth: "92px", textAlign: "center" }}>
                      Página {currentPage} / {totalPages}
                    </span>
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      disabled={currentPage >= totalPages || isLoading || customers.isFetching}
                      onClick={() => handleChangePage(currentPage + 1)}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>
      <Shortcuts
        toggleModalCreate={toggleModalCreate}
        toggleModalImport={toggleModalImport}
        downloadTemplate={downloadTemplate}
      />
    </React.Fragment>
  )
}

export default CustomersList
